<template>
  <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="refreshView">
    <template v-slot:header-options>
      <div class="sub-filters" role="group" aria-label="Filtrer les sujets">
        <button v-for="filter in visibleQuickFilters" :key="filter.label" type="button" class="sub-filter-button" :class="{active: quickFilterLabel === filter.label}" :aria-pressed="quickFilterLabel === filter.label" @click="quickFilterLabel = filter.label">
          {{ filter.label }} <span class="count">{{ countOf(filter) }}</span>
        </button>
      </div>
      <button class="button-icon" title="Ajouter un sujet" @click="$refs.topicModal.openModal()"><span class="fas fa-plus"></span></button>
    </template>
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header field="title" class="col-fill">Sujet</custom-header>
          <custom-header field="publications" class="hide-for-mobile col-fit" :sortable="false">Réseaux</custom-header>
          <custom-header field="funnelStage" class="hide-for-mobile col-fit">Intention</custom-header>
          <custom-header field="cadence" class="hide-for-mobile col-fit">Rythme</custom-header>
          <custom-header field="progress.percent" class="col-fit">Avancement</custom-header>
          <custom-header field="status" class="col-fit">Statut</custom-header>
          <custom-header field="plannedDate" class="hide-for-mobile col-fit">Prévu le</custom-header>
          <custom-header field="actions" class="col-fit" :sortable="false">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="rows().length > 0">
        <tr v-for="topic in rows()" :key="topic.id" class="business-row" @click="$refs.topicModal.openModal(topic)">
          <custom-body field="title" class="col-fill col-truncate cell-strong" :title="topic.title">{{ topic.title }}</custom-body>
          <custom-body field="publications" class="hide-for-mobile col-fit">
            <span class="networks">
              <StatusBadge v-for="publication in topic.publications" :key="publication.id" :status="publication.status" :label="channelLabel(publication.channel)" :title="publicationTitle(publication)" />
              <span v-if="topic.publications.length === 0" class="none">-</span>
            </span>
          </custom-body>
          <custom-body field="funnelStage" class="hide-for-mobile col-fit"><FunnelStageBadge :stage="topic.funnelStage" /></custom-body>
          <custom-body field="cadence" class="hide-for-mobile col-fit">{{ cadenceLabel(topic.cadence) }}</custom-body>
          <custom-body field="progress.percent" class="col-fit"><TopicProgress :progress="topic.progress" /></custom-body>
          <custom-body field="status" class="col-fit"><StatusBadge :status="topic.status" :status-enum="TOPIC_STATUS_ENUM" /></custom-body>
          <custom-body field="plannedDate" class="hide-for-mobile col-fit">{{ formatDate(topic.plannedDate) }}</custom-body>
          <custom-body field="actions" class="col-fit actions">
            <button class="button-icon-custom" title="Modifier" @click.stop="$refs.topicModal.openModal(topic)"><span class="fas fa-pen-to-square"></span></button>
            <button class="button-icon-custom" title="Supprimer" @click.stop="remove(topic)"><span class="fas fa-trash-can"></span></button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row"><td colspan="8" style="text-align: center">{{ emptyText }}</td></tr></tbody>
    </template>
  </CustomTable>
  <TopicModal ref="topicModal" :callback=changed :openPublication=openPublication />
  <PublicationModal ref="publicationModal" :callback=publicationChanged :closedCallback=publicationClosed />
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer ce sujet et ses publications ?" yes="Supprimer" no="Retour"/>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPenToSquare, faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {CustomTableService} from "@/services/CustomTableService";
import {TopicController} from "@/marketing/controllers/TopicController";
import TopicModal from "@/marketing/components/modals/TopicModal.vue";
import PublicationModal from "@/marketing/components/modals/PublicationModal.vue";
import FunnelStageBadge from "@/marketing/components/FunnelStageBadge.vue";
import TopicProgress from "@/marketing/components/TopicProgress.vue";
import {queryRows} from "@/marketing/tableQuery";
import {CADENCE_ENUM, CHANNEL_ENUM, FUNNEL_STAGE_FILTER_ENUM, PUBLICATION_STATUS_ENUM, TEXT_STATUS_ENUM, TOPIC_STATUS_ENUM, formatDate, idOf, labelOf} from "@/marketing/utils";
library.add(faPenToSquare, faPlus, faTrashCan);

// Filtres rapides sur le statut (calculé) du sujet ; le premier est choisi par défaut
const QUICK_FILTERS = [
  {label: "En cours", statuses: ["IDEA", "DRAFTING", "READY", "SCHEDULED"]},
  {label: "Prêts", statuses: ["READY"]},
  {label: "Programmés", statuses: ["SCHEDULED"]},
  {label: "Publiés", statuses: ["PUBLISHED"]},
  {label: "À classer", unclassified: true},
  {label: "De côté", statuses: ["PARKED"]},
  {label: "Tous", statuses: undefined},
];

/**
 * Tableau des sujets (écran Contenus). Le service renvoie tous les sujets du groupe avec le résumé de leurs publications :
 * la recherche, les filtres et les tris s'appliquent donc ici. Un clic ouvre le sujet.
 * changedCallback est appelé après toute modification (pour recalculer la répartition par intention).
 */
export default {
  name: 'TopicTable',
  components: {PublicationModal, TopicModal, StatusBadge, ConfirmModal, CustomBody, CustomHeader, CustomTable, FunnelStageBadge, TopicProgress},
  props: ['changedCallback'],
  data() {
    return {
      TOPIC_STATUS_ENUM,
      data: [],
      quickFilters: QUICK_FILTERS,
      quickFilterLabel: QUICK_FILTERS[0].label,
      elements: [
        {label: "Sujet", value: "title", type: "text"},
        {label: "Intention", value: "funnelStage", type: "select", values: FUNNEL_STAGE_FILTER_ENUM},
        {label: "Rythme", value: "cadence", type: "select", values: CADENCE_ENUM},
        {label: "Statut", value: "status", type: "select", values: TOPIC_STATUS_ENUM},
        {label: "Avancement (%)", value: "progress.percent", type: "number"},
        {label: "Prévu le", value: "plannedDate", type: "date"},
        {label: "Créé le", value: "createdAt", type: "date"},
        {label: "Actions", value: undefined, type: undefined},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "createdAt", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
      }
    };
  },
  computed: {
    // « À classer » n'est proposé que s'il y a des contenus importés à classer (ou s'il est choisi)
    visibleQuickFilters() {
      return this.quickFilters.filter(filter => !filter.unclassified || filter.label === this.quickFilterLabel || this.countOf(filter) > 0);
    },
    emptyText() {
      return this.data.length === 0 ? "Aucun sujet : ajoutez votre première idée avec le bouton +" : "Aucun sujet ne correspond";
    },
  },
  async mounted() {
    // Arrivée depuis l'écran Connexions : on montre les publications importées à classer
    if (this.$route?.query?.filtre === 'a-classer') this.quickFilterLabel = "À classer";

    // Toutes les lignes sont affichées : pas de pagination
    const pagination = this.$refs.table.$refs.paginationArray;
    pagination.paginationInputs.pages.readOnly = true;
    pagination.isNextReloadCancel = true;
    pagination.paginationInputs.pages.value = undefined;
    pagination.index = undefined;

    await this.loadData();
  },
  methods: {
    formatDate,
    cadenceLabel(cadence) {
      return cadence === CADENCE_ENUM.WEEKLY.value ? "Semaine" : (cadence === CADENCE_ENUM.PILLAR.value ? "Mois" : "-");
    },
    channelLabel(channel) {
      return labelOf(CHANNEL_ENUM, channel, channel);
    },
    publicationTitle(publication) {
      return `${this.channelLabel(publication.channel)} : ${labelOf(PUBLICATION_STATUS_ENUM, publication.status)} (texte : ${labelOf(TEXT_STATUS_ENUM, publication.textStatus).toLowerCase()})`;
    },
    matchesQuickFilter(topic, filter) {
      if (filter.unclassified) return !topic.funnelStage;
      return !filter.statuses || filter.statuses.includes(topic.status);
    },
    countOf(filter) {
      return this.data.filter(topic => this.matchesQuickFilter(topic, filter)).length;
    },
    // Lignes affichées : filtre rapide, recherche libre, puis filtres et tris avancés du tableau
    rows() {
      const filter = this.quickFilters.find(item => item.label === this.quickFilterLabel);
      let rows = this.data.filter(topic => this.matchesQuickFilter(topic, filter));
      if (!this.$refs.table) return rows;
      rows = this.$refs.table.$refs.searchArray.simpleFilter(rows);
      const state = CustomTableService.data.get(this.$refs.table.tableId);
      return state ? queryRows(rows, state.currentFilters, state.currentSort) : rows;
    },
    // Filtres et tris sont appliqués à l'affichage : rien à recharger
    async refreshView() {},
    async loadData() {
      this.$refs.table?.startLoading();
      const topics = await TopicController.getTable();
      if (topics && !topics.errors) {
        this.data = topics.map(topic => ({...topic, id: idOf(topic), publications: (topic.publications ?? []).map(publication => ({...publication, id: idOf(publication)}))}));
      }
      this.$refs.table?.stopLoading();
    },
    async changed() {
      await this.loadData();
      if (this.changedCallback) this.changedCallback();
    },
    // Une publication modifiée change l'avancement du sujet ouvert (s'il y en a un) et du tableau
    async publicationChanged() {
      await this.$refs.topicModal.reloadPublications();
      await this.changed();
    },
    openPublication(publicationId) {
      this.$refs.publicationModal.openModal(publicationId);
    },
    // La modale d'une publication se referme : le sujet ouvert en dessous redevient la modale active
    publicationClosed() {
      this.$refs.topicModal?.refocus();
    },
    async remove(topic) {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => TopicController.delete(topic.id));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Sujet supprimé");
        await this.changed();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';

// Le nombre de sujets du filtre, en retrait du libellé
.count
  opacity 0.7
  font-variant-numeric tabular-nums

.networks
  display inline-flex
  flex-wrap wrap
  gap 4px

// Les deux icônes d'action restent côte à côte (la ligne garde une hauteur compacte)
.actions
  white-space nowrap

.none
  color CTRL_TEXT_MUTED
</style>
